import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  ArrowRight,
  Braces,
  Check,
  Database,
  FileArchive,
  FileCode2,
  FolderUp,
  Globe2,
  Settings2,
  ShieldCheck,
  TerminalSquare,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const orderUrl = 'https://961host.net/order/index.php/store/web-hosting';

const RuntimeMark = ({ children, className = '' }) => (
  <span className={`inline-flex h-10 min-w-10 items-center justify-center rounded-lg border border-white/10 bg-white/[0.07] px-2 font-mono text-sm font-semibold text-white ${className}`}>
    {children}
  </span>
);

const DeploymentGraphic = () => (
  <div className="relative mx-auto max-w-xl" aria-label="Application deployment workflow illustration">
    <div className="absolute -inset-8 bg-primary/20 blur-3xl" aria-hidden="true" />
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900 shadow-2xl shadow-slate-950/40">
      <div className="flex items-center gap-2 border-b border-white/10 bg-white/[0.03] px-5 py-4">
        <span className="h-2.5 w-2.5 rounded-full bg-slate-500" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-500" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-500" />
        <span className="ml-2 font-mono text-xs text-slate-400">deployment / application</span>
      </div>
      <div className="p-5 sm:p-7">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-5">
          <div className="rounded-xl border border-white/10 bg-slate-950/60 p-4 sm:p-5">
            <FileCode2 className="mb-4 h-6 w-6 text-sky-300" />
            <p className="font-mono text-xs text-slate-300">your application</p>
            <div className="mt-3 space-y-2">
              <div className="h-1.5 w-4/5 rounded bg-slate-700" />
              <div className="h-1.5 w-3/5 rounded bg-slate-700" />
              <div className="h-1.5 w-2/3 rounded bg-slate-700" />
            </div>
          </div>
          <div className="flex flex-col items-center gap-1 text-primary">
            <span className="h-px w-5 bg-primary sm:w-9" />
            <ArrowRight className="h-4 w-4" />
            <span className="h-px w-5 bg-primary sm:w-9" />
          </div>
          <div className="rounded-xl border border-primary/30 bg-primary/10 p-4 sm:p-5">
            <Settings2 className="mb-4 h-6 w-6 text-blue-300" />
            <p className="font-mono text-xs text-blue-100">cPanel app setup</p>
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between text-[10px] text-slate-300"><span>runtime</span><span>selected</span></div>
              <div className="flex items-center justify-between text-[10px] text-slate-300"><span>root</span><span>configured</span></div>
              <div className="flex items-center justify-between text-[10px] text-slate-300"><span>URL</span><span>assigned</span></div>
            </div>
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3 rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-400 text-slate-950"><Check className="h-4 w-4" strokeWidth={3} /></span>
          <span className="font-mono text-xs text-emerald-100">application available at your domain</span>
        </div>
      </div>
    </div>
  </div>
);

export const VibeCodingPage = () => {
  const runtimes = [
    { name: 'Node.js', description: 'Web applications, APIs, and server-side JavaScript.', mark: 'node' },
    { name: 'Python', description: 'Framework-based sites and Python web applications.', mark: 'py' },
    { name: 'Ruby', description: 'Ruby applications with a conventional web deployment workflow.', mark: 'rb' },
  ];

  const capabilities = [
    { icon: Settings2, title: 'cPanel application setup', description: 'Create and configure your application from the hosting control panel.' },
    { icon: FolderUp, title: 'File-based deployment', description: 'Upload your project with File Manager or FTP, then work from the application root.' },
    { icon: Database, title: 'Database tools', description: 'Hosting plans include SQL databases for applications that need persistent data.' },
    { icon: ShieldCheck, title: 'HTTPS with SSL', description: "Let's Encrypt SSL is included with hosting plans for secure application URLs." },
    { icon: Globe2, title: 'Use your own domain', description: 'Connect an application URL to the domain or subdomain you manage in cPanel.' },
    { icon: TerminalSquare, title: 'Dependency workflow', description: 'Install the dependencies declared by your project through the application environment.' },
  ];

  const journey = [
    { icon: FileArchive, title: 'Prepare the project', description: 'Keep your source files and dependency manifest ready for upload.' },
    { icon: Settings2, title: 'Create the application', description: 'Choose the runtime, application root, startup entry point, and URL in cPanel.' },
    { icon: FolderUp, title: 'Upload and install', description: 'Place the project in its application root and install its required packages.' },
    { icon: Globe2, title: 'Restart and verify', description: 'Restart the application from its cPanel interface, then visit its assigned URL.' },
  ];

  const questions = [
    { question: 'Which applications is this best for?', answer: 'It is intended for developers deploying Node.js, Python, or Ruby web applications on a shared hosting plan with cPanel access.' },
    { question: 'How do I deploy my code?', answer: 'Create an application in cPanel, upload the project to its application root through File Manager or FTP, install dependencies, and restart the application.' },
    { question: 'Can I use a database and a custom domain?', answer: 'Hosting plans include SQL databases and domain management tools. Configure your application URL and database connection details from your hosting account.' },
    { question: 'Where can I get help with my account?', answer: 'Existing customers can open a support ticket through the client area. If you are choosing a plan, contact 961Host before ordering.' },
  ];

  return (
    <div className="min-h-screen pt-16 overflow-x-hidden">
      <section className="relative overflow-hidden bg-foreground py-16 sm:py-20 lg:py-24">
        <div className="absolute inset-0 opacity-30" aria-hidden="true" style={{ backgroundImage: 'linear-gradient(rgba(148,163,184,.12) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,.12) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
        <div className="absolute -right-40 top-0 h-96 w-96 rounded-full bg-primary/25 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_0.9fr] lg:px-8">
          <div>
            <p className="mb-5 font-mono text-sm font-medium uppercase tracking-[0.18em] text-blue-300">Developer application hosting</p>
            <h1 className="max-w-3xl text-4xl font-semibold leading-tight text-white sm:text-5xl lg:text-6xl">Deploy the application you built.</h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">VibeCoders gives Node.js, Python, and Ruby developers a practical path from local project to a live application, managed through the cPanel tools included with 961Host hosting.</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button size="lg" variant="primary-light" asChild data-testid="vibecoders-view-plans-btn">
                <a href={orderUrl} target="_blank" rel="noopener noreferrer">View Hosting Plans <ArrowRight className="h-5 w-5" /></a>
              </Button>
              <Button size="lg" variant="outline-dark" asChild>
                <a href="#deployment">How deployment works</a>
              </Button>
            </div>
            <p className="mt-6 text-sm text-slate-400">Bring your code, dependencies, and domain. Manage the application from cPanel.</p>
          </div>
          <DeploymentGraphic />
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="font-mono text-sm font-medium uppercase tracking-[0.16em] text-primary">Built for application work</p>
            <h2 className="mt-3 text-3xl font-semibold text-foreground sm:text-4xl">Choose the runtime that fits your project.</h2>
            <p className="mt-4 text-lg leading-7 text-muted-foreground">Use familiar application tooling while keeping site, domain, files, and databases together in your hosting account.</p>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {runtimes.map((runtime) => (
              <Card key={runtime.name} className="border-border shadow-sm">
                <CardContent className="p-6">
                  <RuntimeMark>{runtime.mark}</RuntimeMark>
                  <h3 className="mt-5 text-xl font-semibold text-foreground">{runtime.name}</h3>
                  <p className="mt-2 leading-6 text-muted-foreground">{runtime.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <div>
              <p className="font-mono text-sm font-medium uppercase tracking-[0.16em] text-primary">The hosting foundation</p>
              <h2 className="mt-3 text-3xl font-semibold text-foreground sm:text-4xl">The tools around your application matter.</h2>
              <p className="mt-4 leading-7 text-muted-foreground">VibeCoders is for projects that benefit from a straightforward hosting account and a clear application configuration workflow, not a separate platform to learn.</p>
            </div>
            <div className="grid gap-x-8 gap-y-8 sm:grid-cols-2">
              {capabilities.map((capability) => {
                const Icon = capability.icon;
                return (
                  <div key={capability.title} className="flex gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
                    <div>
                      <h3 className="font-semibold text-foreground">{capability.title}</h3>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground">{capability.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section id="deployment" className="scroll-mt-16 bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="font-mono text-sm font-medium uppercase tracking-[0.16em] text-primary">Deployment journey</p>
            <h2 className="mt-3 text-3xl font-semibold text-foreground sm:text-4xl">A clear route to a live application.</h2>
            <p className="mt-4 leading-7 text-muted-foreground">The exact startup file and dependencies depend on your framework. The process stays centered on the application interface in cPanel.</p>
          </div>
          <ol className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {journey.map((step, index) => {
              const Icon = step.icon;
              return (
                <li key={step.title} className="relative border-t-2 border-primary/20 pt-5">
                  <span className="font-mono text-sm font-semibold text-primary">0{index + 1}</span>
                  <Icon className="mt-5 h-6 w-6 text-foreground" />
                  <h3 className="mt-4 font-semibold text-foreground">{step.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">{step.description}</p>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      <section className="bg-slate-950 py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr] lg:px-8">
          <div>
            <p className="font-mono text-sm font-medium uppercase tracking-[0.16em] text-blue-300">Before you order</p>
            <h2 className="mt-3 text-3xl font-semibold text-white sm:text-4xl">Make sure the workflow fits your project.</h2>
            <p className="mt-4 leading-7 text-slate-300">This hosting approach suits developers who are comfortable preparing their application, managing its files, and configuring its runtime settings in cPanel.</p>
            <Button className="mt-7" variant="outline-dark" asChild>
              <Link to="/contact">Ask about your application <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </div>
          <div className="divide-y divide-white/10 border-y border-white/10">
            {questions.map((item) => (
              <div key={item.question} className="py-5 first:pt-0 last:pb-0">
                <h3 className="font-semibold text-white">{item.question}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-300">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-primary py-16 text-center sm:py-20">
        <div className="mx-auto max-w-3xl px-4">
          <Braces className="mx-auto h-10 w-10 text-white/85" />
          <h2 className="mt-5 text-3xl font-semibold text-white sm:text-4xl">Ready to host your application?</h2>
          <p className="mt-4 text-lg leading-7 text-blue-100">Choose a 961Host plan, then configure your application from the cPanel tools included with your account.</p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Button size="lg" variant="primary-light" asChild data-testid="vibecoders-start-btn">
              <a href={orderUrl} target="_blank" rel="noopener noreferrer">Explore Hosting Plans <ArrowRight className="h-5 w-5" /></a>
            </Button>
            <Button size="lg" variant="outline-dark" asChild>
              <Link to="/contact">Contact 961Host</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
